import { createSelector } from '@reduxjs/toolkit';

import { MediaText, TextStyle } from 'editor/src/store/design/types';
import { getSelectionFillMode, getTextStyle, isTextStyleModified } from 'editor/src/store/design/util/textStyles';
import type { RootState } from 'editor/src/store/index';

import getSelectedMediaElementsObjects from './getSelectedMediaElementsObjects';

/**
 * GAPI-61895: which style the selected texts follow, for the toolbar Style control.
 * `none`: no text selected, or the texts follow no style. `mixed`: the texts follow
 * different styles (or some follow none). `style`: they all follow `style`; `modified`
 * when at least one of them no longer looks like it.
 */
export type SelectionTextStyle =
  | { kind: 'none' }
  | { kind: 'mixed' }
  | { kind: 'style'; style: TextStyle; modified: boolean };

const NONE: SelectionTextStyle = { kind: 'none' };
const MIXED: SelectionTextStyle = { kind: 'mixed' };

const getSelectionTextStyle = createSelector(
  getSelectedMediaElementsObjects,
  (state: RootState) => state.design.designData,
  getSelectionFillMode,
  // under the CMYK lock an ink is told apart from its hex only once the print profile has loaded
  (state: RootState) => state.hostSettings.cmykOnly && !!state.app.colorProfileLoaded,
  (selected, designData, fillMode): SelectionTextStyle => {
    const texts = selected.filter((element): element is MediaText => element?.type === 'text');
    if (!texts.length) {
      return NONE;
    }
    const styleIds = new Set(texts.map((text) => text.extra.textStyleId));
    if (styleIds.size > 1) {
      return MIXED;
    }
    const style = getTextStyle(designData, texts[0].extra.textStyleId);
    if (!style) {
      return NONE;
    }
    return { kind: 'style', style, modified: texts.some((text) => isTextStyleModified(style, text, fillMode)) };
  },
);

export default getSelectionTextStyle;
